<script setup lang="ts">
import type { TableColumn, TableRow } from '@nuxt/ui'
import type { Customer } from '@/types'
import { Head, Link, router } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { dashboardLayout } from '@/lib/layouts'
import { adminCustomerPath, newAdminCustomerPath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  customers: Customer[]
}>()

const title = 'Customers'
const search = ref('')
const status = ref<'all' | 'active' | 'inactive'>('all')

const statusItems = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
]

const filtered = computed(() => {
  const query = search.value.trim().toLowerCase()

  return props.customers.filter((customer) => {
    const matchesQuery = !query || customer.name.toLowerCase().includes(query)
    const matchesStatus
      = status.value === 'all'
        || (status.value === 'active' ? customer.active : !customer.active)

    return matchesQuery && matchesStatus
  })
})

const columns: TableColumn<Customer>[] = [
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'active', header: 'Status' },
  { accessorKey: 'created_at', header: 'Created' },
  { id: 'actions' },
]

function openClient(_event: Event, row: TableRow<Customer>) {
  router.visit(adminCustomerPath(row.original.id))
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString()
}
</script>

<template>
  <Head :title="title" />

  <UDashboardPanel id="customers">
    <template #header>
      <UDashboardNavbar :title="title">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>

        <template #right>
          <UButton
            :to="newAdminCustomerPath()"
            :as="Link"
            label="New customer"
            icon="i-lucide-plus"
          />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div
        v-if="customers.length === 0"
        class="flex flex-col items-center justify-center gap-4 py-20 text-center"
      >
        <UIcon name="i-lucide-building-2" class="text-dimmed size-10" />
        <div>
          <p class="text-highlighted font-medium">
            No customers yet
          </p>
          <p class="text-muted text-sm">
            Add a customer to grant product instances and issue licenses.
          </p>
        </div>
        <UButton
          :to="newAdminCustomerPath()"
          :as="Link"
          label="New customer"
          icon="i-lucide-plus"
        />
      </div>

      <div v-else class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-1 flex-wrap items-center gap-3">
            <UInput
              v-model="search"
              icon="i-lucide-search"
              placeholder="Search customers…"
              class="w-full max-w-sm"
            />
            <USelect
              v-model="status"
              :items="statusItems"
              value-key="value"
              class="w-36"
            />
          </div>
          <p class="text-muted shrink-0 text-sm tabular-nums">
            {{ filtered.length }} of {{ customers.length }}
          </p>
        </div>

        <UTable
          :data="filtered"
          :columns="columns"
          :on-select="openClient"
          class="ring-default rounded-lg ring"
          :ui="{ tr: 'cursor-pointer' }"
        >
          <template #name-cell="{ row }">
            <span class="text-highlighted font-medium">{{ row.original.name }}</span>
          </template>

          <template #active-cell="{ row }">
            <UBadge
              :color="row.original.active ? 'success' : 'neutral'"
              variant="subtle"
              size="sm"
            >
              {{ row.original.active ? 'Active' : 'Inactive' }}
            </UBadge>
          </template>

          <template #created_at-cell="{ row }">
            <span class="text-muted text-sm">{{ formatDate(row.original.created_at) }}</span>
          </template>

          <template #actions-header>
            <span class="sr-only">Actions</span>
          </template>

          <template #actions-cell>
            <div class="flex justify-end">
              <UIcon name="i-lucide-chevron-right" class="text-dimmed size-4" />
            </div>
          </template>

          <template #empty>
            <div class="text-muted py-6 text-center text-sm">
              No customers match your filters.
            </div>
          </template>
        </UTable>
      </div>
    </template>
  </UDashboardPanel>
</template>
